// A clock time for a list, such as the Orders screen's Time column.
//
// Shown in this device's own time zone: a till stands in its shop, and the
// shop's clock is the one a cashier reads. A dashboard screen passes the
// restaurant's time zone instead, so an owner abroad reads the shop's clock,
// never the browser's. Latin digits in both languages, as
// every number on the till is.

import { isArabic, type Language } from '../i18n/messages';

export function formatClock(
	iso: string | undefined,
	language: Language,
	timeZone?: string,
): string {
	if (!iso) return '';
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return '';
	return new Intl.DateTimeFormat(isArabic(language) ? 'ar-EG-u-nu-latn' : 'en-US', {
		hour: 'numeric',
		minute: '2-digit',
		timeZone: knownZone(timeZone),
	}).format(at);
}

/** The zone when this browser knows it, else undefined: the device's own. */
function knownZone(timeZone: string | undefined): string | undefined {
	if (!timeZone) return undefined;
	try {
		new Intl.DateTimeFormat('en-US', { timeZone });
		return timeZone;
	} catch {
		return undefined;
	}
}

/** The calendar day of a moment in a zone, as a number of days, to count between two. */
function dayNumber(at: Date, timeZone: string | undefined): number {
	if (!timeZone) return Date.UTC(at.getFullYear(), at.getMonth(), at.getDate()) / 86_400_000;
	// Read by part, never by a pattern: a locale's date shape is not fixed.
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone,
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
	}).formatToParts(at);
	const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
	return Date.UTC(part('year'), part('month') - 1, part('day')) / 86_400_000;
}

/**
 * When something last happened, for a list read days later: "Today 1:10 PM",
 * "Yesterday 9:45 PM", or the date alone ("22 Sep") once it is older than
 * that. The words come from the caller, in its language. Days and clock are
 * the device's own, or those of timeZone when the caller names one.
 */
export function formatWhen(
	iso: string | undefined,
	language: Language,
	words: { today: string; yesterday: string },
	now: Date = new Date(),
	timeZone?: string,
): string {
	if (!iso) return '';
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return '';
	const zone = knownZone(timeZone);
	const days = Math.round(dayNumber(now, zone) - dayNumber(at, zone));
	// A moment a little ahead of this device's clock (a server's time, near
	// midnight) is today, not a bare date.
	if (days <= 0) return `${words.today} ${formatClock(iso, language, zone)}`;
	if (days === 1) return `${words.yesterday} ${formatClock(iso, language, zone)}`;
	// Day first in both languages. Built from parts because en-GB now writes
	// September as "Sept", and en-US puts the month first.
	const parts = new Intl.DateTimeFormat(isArabic(language) ? 'ar-EG-u-nu-latn' : 'en-US', {
		day: 'numeric',
		month: isArabic(language) ? 'long' : 'short',
		timeZone: zone,
	}).formatToParts(at);
	const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
	return `${part('day')} ${part('month')}`;
}

/** A day with its year, for when something was added: "12 Sep 2026". */
export function formatDate(iso: string | undefined, language: Language): string {
	if (!iso) return '';
	const at = new Date(iso);
	if (Number.isNaN(at.getTime())) return '';
	const parts = new Intl.DateTimeFormat(isArabic(language) ? 'ar-EG-u-nu-latn' : 'en-US', {
		day: 'numeric',
		month: isArabic(language) ? 'long' : 'short',
		year: 'numeric',
	}).formatToParts(at);
	const part = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
	return `${part('day')} ${part('month')} ${part('year')}`;
}
